import { useEffect, useRef, useState } from 'react';

interface CookieConsentProps {
  onConsentChanged?: (consent: 'all' | 'necessary' | 'personalizza') => void;
  forceShow?: boolean;
  onCloseForceShow?: () => void;
}

type AnalyticsPreference = 'granted' | 'denied';
type GoogleFc = {
  callbackQueue: Array<unknown>;
  showRevocationMessage?: () => void;
  getGoogleConsentModeValues?: () => { analyticsStoragePurposeConsentStatus: number };
};

declare global {
  interface Window {
    googlefc?: GoogleFc;
    __tcfapi?: (...args: unknown[]) => void;
  }
}

// A functional preference only: this never grants advertising consent.
export const ANALYTICS_PREFERENCE_KEY = 'sbarazzino:analytics:consent:v1';
const GOOGLE_CMP_WAIT_MS = 4000;

function readAnalyticsPreference(): AnalyticsPreference | null {
  try {
    const saved = window.localStorage.getItem(ANALYTICS_PREFERENCE_KEY);
    return saved === 'granted' || saved === 'denied' ? saved : null;
  } catch {
    return null;
  }
}

function updateAnalyticsPreference(value: AnalyticsPreference): void {
  try {
    window.localStorage.setItem(ANALYTICS_PREFERENCE_KEY, value);
  } catch {
    // An in-memory choice still applies for the current page.
  }
  window.gtag?.('consent', 'update', { analytics_storage: value });
  if (value === 'granted') {
    // The initial page_view was sent with default denied. Record the first
    // consented view only after forwarding the consent update.
    window.gtag?.('event', 'page_view', {
      page_location: window.location.href,
      page_title: document.title,
    });
  }
}

/**
 * The Google IAB CMP remains the authority for advertising. While it is not
 * delivered (e.g. the AdSense site is awaiting approval), show a separate,
 * analytics-only opt-in instead of silently disabling every product KPI.
 * No analytics event can pass the consent gate before the user accepts.
 */
export default function CookieConsent({
  forceShow = false,
  onCloseForceShow,
}: CookieConsentProps) {
  const [showAnalyticsChoice, setShowAnalyticsChoice] = useState(false);
  const cmpControlsAnalytics = useRef(false);
  const cmpActive = useRef(false);

  useEffect(() => {
    try {
      localStorage.removeItem('sbarazzo_cookie_consent');
      localStorage.removeItem('sbarazzo_cookie_analytics');
      localStorage.removeItem('sbarazzo_cookie_ads');
    } catch {
      // Storage can be disabled; users must still be able to play.
    }

    const googlefc: GoogleFc = window.googlefc || { callbackQueue: [] };
    window.googlefc = googlefc;
    googlefc.callbackQueue = googlefc.callbackQueue || [];
    const syncCmpAnalyticsConsent = () => {
      cmpActive.current = true;
      const status = googlefc.getGoogleConsentModeValues?.()?.analyticsStoragePurposeConsentStatus;
      if (status === 1 || status === 2) {
        cmpControlsAnalytics.current = true;
        // A certified CMP decision supersedes a previously stored local one.
        try {
          window.localStorage.removeItem(ANALYTICS_PREFERENCE_KEY);
        } catch {
          // Restricted storage must not block consent synchronization.
        }
        window.gtag?.('consent', 'update', {
          analytics_storage: status === 1 ? 'granted' : 'denied',
        });
        setShowAnalyticsChoice(false);
      }
    };
    googlefc.callbackQueue.push({ CONSENT_MODE_DATA_READY: syncCmpAnalyticsConsent });
    // Consent may change again after the initial CMP decision. Listen for
    // subsequent TCF updates so product events stop after withdrawal too.
    googlefc.callbackQueue.push({
      CONSENT_API_READY: () => {
        window.__tcfapi?.('addEventListener', 2, (_data: unknown, success: unknown) => {
          if (success) syncCmpAnalyticsConsent();
        });
      },
    });

    const timer = window.setTimeout(() => {
      // Google CMP may appear asynchronously. Never restore a local grant
      // before determining whether its certified consent UI owns the choice.
      if (cmpControlsAnalytics.current || cmpActive.current ||
          typeof window.__tcfapi === 'function' ||
          typeof window.googlefc?.showRevocationMessage === 'function') return;
      const saved = readAnalyticsPreference();
      if (saved) {
        updateAnalyticsPreference(saved);
        return;
      }
      setShowAnalyticsChoice(true);
    }, GOOGLE_CMP_WAIT_MS);

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!forceShow) return;

    if (typeof window.googlefc?.showRevocationMessage === 'function' &&
        (cmpControlsAnalytics.current || !readAnalyticsPreference())) {
      // Google documents callbackQueue as the supported API invocation route.
      window.googlefc.callbackQueue.push({
        CONSENT_API_READY: () => window.googlefc?.showRevocationMessage?.(),
      });
    } else if (cmpControlsAnalytics.current) {
      // Never show a local grant button when the certified CMP is authoritative.
      console.warn('[Consent] Google CMP privacy controls are currently unavailable.');
    } else {
      // Also lets existing users revoke an analytics-only preference at any time.
      setShowAnalyticsChoice(true);
    }
    onCloseForceShow?.();
  }, [forceShow, onCloseForceShow]);

  const saveChoice = (choice: AnalyticsPreference) => {
    if (cmpControlsAnalytics.current) return;
    updateAnalyticsPreference(choice);
    setShowAnalyticsChoice(false);
  };

  if (!showAnalyticsChoice) return null;

  return (
    <section
      role="region"
      aria-labelledby="analytics-consent-title"
      aria-describedby="analytics-consent-description"
      className="fixed bottom-3 left-3 right-3 z-[300] mx-auto max-w-xl rounded-2xl border border-white/20 bg-slate-950 p-5 text-white shadow-2xl sm:bottom-6 sm:p-6"
    >
      <h2 id="analytics-consent-title" className="text-lg font-bold">Statistiche di utilizzo</h2>
      <p id="analytics-consent-description" className="mt-2 text-sm leading-relaxed text-slate-200">
        Vuoi aiutarci a migliorare Sbarazzino? Con il tuo consenso Google Analytics
        misura visite e utilizzo del gioco, comprese partite iniziate e completate.
        Non inviamo nomi, email o codici delle partite. Questa scelta non autorizza
        i cookie pubblicitari e puoi modificarla da Opzioni → Privacy &amp; Cookie.
      </p>
      <p className="mt-2 text-xs text-slate-300">
        Leggi la <a className="underline" href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        {' e la '}<a className="underline" href="/cookie-policy.html" target="_blank" rel="noopener noreferrer">Cookie Policy</a>.
      </p>
      <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button type="button" onClick={() => saveChoice('denied')}
          className="rounded-xl border border-slate-400 px-5 py-3 text-sm font-semibold hover:bg-white/10">
          Rifiuta
        </button>
        <button type="button" onClick={() => saveChoice('granted')}
          className="rounded-xl bg-emerald-400 px-5 py-3 text-sm font-bold text-slate-950 hover:bg-emerald-300">
          Accetta le statistiche
        </button>
      </div>
    </section>
  );
}
